import { useMemo } from 'react';

import * as styles from './CustomTabInlineEmbed.css';
import { CustomTab } from './types';

interface CustomTabInlineEmbedProps {
  tab: CustomTab;
  eventId?: string | number;
  eventTags?: Array<{ key: string; value: string }>;
  listingId?: string | number;
}

export function substituteTabTokens(
  content: string,
  eventId: string | number | undefined,
  tags: Array<{ key: string; value: string }> | undefined,
  listingId?: string | number
): string {
  let result = content;
  if (eventId != null) {
    result = result.replace(/\{eventId\}/gi, String(eventId));
  }
  if (listingId != null) {
    result = result.replace(/\{listingId\}/gi, String(listingId));
  }
  if (tags && tags.length > 0) {
    result = result.replace(/\{EventTag:([^}]+)\}/g, (_match, tagName) => {
      const found = tags.find(
        (t) => t.key.toLowerCase() === tagName.toLowerCase()
      );
      return found ? found.value : '';
    });
  }
  return result;
}

export const CustomTabInlineEmbed = ({
  tab,
  eventId,
  eventTags,
  listingId,
}: CustomTabInlineEmbedProps) => {
  const processedUrl = useMemo(() => {
    if (!tab.url) return '';
    return substituteTabTokens(tab.url, eventId, eventTags, listingId);
  }, [tab.url, eventId, eventTags, listingId]);

  const processedSnippet = useMemo(() => {
    if (!tab.snippet) return '';
    return substituteTabTokens(tab.snippet, eventId, eventTags, listingId);
  }, [tab.snippet, eventId, eventTags, listingId]);

  if (tab.tabType === 'UrlEmbed' && processedUrl) {
    return (
      <div className={styles.root}>
        <iframe
          src={processedUrl}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          className={styles.iframe}
          title={tab.label}
        />
      </div>
    );
  }

  if (tab.tabType === 'SnippetEmbed' && processedSnippet) {
    return (
      <div className={styles.root}>
        <iframe
          srcDoc={processedSnippet}
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          className={styles.iframe}
          title={tab.label}
        />
      </div>
    );
  }

  return (
    <div className={styles.empty}>
      No embed content configured for this tab.
    </div>
  );
};
